/**
 * AcceptInvite.jsx - melta UI
 * 招待リンクからの組織参加ページ
 * URL: /invite/:token
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getInvitation, acceptInvitation } from '../services/userApi';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Badge } from '../components/ui';

const ROLE_LABELS = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate  = useNavigate();
  const { isLoggedIn, loading: authLoading } = useAuth();

  const [invitation,   setInvitation]  = useState(null);
  const [loadError,    setLoadError]   = useState('');
  const [accepting,    setAccepting]   = useState(false);
  const [acceptError,  setAcceptError] = useState('');
  const [done,         setDone]        = useState(false);

  useEffect(() => {
    if (!token) { setLoadError('招待リンクが無効です'); return; }
    getInvitation(token)
      .then(data => setInvitation(data))
      .catch(() => setLoadError('招待リンクが無効または期限切れです'));
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      await acceptInvitation({ token, transferData: false });
      setDone(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (e) {
      setAcceptError(e.message || '承認に失敗しました');
    } finally {
      setAccepting(false);
    }
  };

  // ローディング中
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <div className="py-8">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900">読み込み中...</h1>
          </div>
        </Card>
      </div>
    );
  }

  // エラー
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <div className="py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="xCircle" className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">招待リンクエラー</h1>
            <p className="text-slate-500 mb-6">{loadError}</p>
            <Link
              to="/login"
              className="text-primary-500 hover:text-primary-700 font-medium"
            >
              ログインページへ
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // 招待確認中
  if (!invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <div className="py-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="mail" className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900">招待を確認中...</h1>
          </div>
        </Card>
      </div>
    );
  }

  // 参加完了
  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <div className="py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="checkCircle" className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">参加完了！</h1>
            <p className="text-slate-500">「{invitation.orgName}」に参加しました</p>
            <p className="text-sm text-slate-400 mt-4">ホーム画面へ移動します...</p>
          </div>
        </Card>
      </div>
    );
  }

  // 未ログイン時
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="mail" className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">招待が届いています</h1>
            <p className="text-slate-500">
              <strong className="text-slate-900">{invitation.orgName}</strong> から
              {invitation.inviterName && ` ${invitation.inviterName} さんより`}
              招待されています
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 space-y-3 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">組織名</span>
              <span className="text-sm font-medium text-slate-900">{invitation.orgName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">役割</span>
              <Badge variant="primary">{ROLE_LABELS[invitation.role]}</Badge>
            </div>
          </div>

          <Alert variant="info" className="mb-6">
            参加するにはログインまたは新規登録が必要です
          </Alert>

          <div className="space-y-3">
            <Link
              to={`/login?redirect=/invite/${token}`}
              className="block w-full"
            >
              <Button className="w-full">
                <Icon name="login" className="w-4 h-4" />
                ログインして参加
              </Button>
            </Link>
            <Link
              to={`/register?redirect=/invite/${token}`}
              className="block w-full"
            >
              <Button variant="secondary" className="w-full">
                <Icon name="user" className="w-4 h-4" />
                新規登録して参加
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // ログイン済み
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="mail" className="w-8 h-8 text-primary-500" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">組織への招待</h1>
          <p className="text-slate-500">
            <strong className="text-slate-900">{invitation.orgName}</strong> から招待されています
          </p>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">組織名</span>
            <span className="text-sm font-medium text-slate-900">{invitation.orgName}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">役割</span>
            <Badge variant="primary">{ROLE_LABELS[invitation.role]}</Badge>
          </div>
          {invitation.inviterName && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">招待者</span>
              <span className="text-sm font-medium text-slate-900">{invitation.inviterName}</span>
            </div>
          )}
        </div>

        <Alert variant="warning" className="mb-6">
          <div className="flex items-start gap-2">
            <Icon name="alertTriangle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>参加すると現在の個人データは組織に移行されます</span>
          </div>
        </Alert>

        {acceptError && (
          <Alert variant="error" className="mb-4">
            {acceptError}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={() => navigate('/')}
            disabled={accepting}
            className="flex-1"
          >
            キャンセル
          </Button>
          <Button
            onClick={handleAccept}
            loading={accepting}
            className="flex-1"
          >
            <Icon name="checkCircle" className="w-4 h-4" />
            参加する
          </Button>
        </div>
      </Card>
    </div>
  );
}
